import { safeValue } from '@shared/utils/resource';
import { HttpClient, httpResource } from '@angular/common/http';
import { computed, inject, Service } from '@angular/core';
import { CreateCredentialRequest } from '@shared/types/dto/credential/create-credential.request';
import { CredentialDto } from '@shared/types/dto/credential/credential.dto';
import { AuthType } from '@shared/types/dto/credential/auth-type.enum';
import { UpdateCredentialRequest } from '@shared/types/dto/credential/update-credential.request';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { GroupService } from './group.service';

@Service()
export class CredentialService {
  private http = inject(HttpClient);
  private groupService = inject(GroupService);

  readonly resource = httpResource<CredentialDto[]>(() => {
    const gid = this.groupService.currentGroupId();
    return gid
      ? `${environment.API_HOST}/groups/${gid}/credentials`
      : undefined;
  });

  readonly list = computed(() => safeValue(this.resource) ?? []);

  filteredBy(authTypes: () => AuthType[]) {
    return computed(() => {
      const allowed = authTypes();
      return this.list().filter((c) => allowed.includes(c.authType));
    });
  }

  async create(body: CreateCredentialRequest): Promise<CredentialDto> {
    const gid = this.groupService.currentGroupId();
    const dto = await firstValueFrom(
      this.http.post<CredentialDto>(
        `${environment.API_HOST}/groups/${gid}/credentials`,
        body,
      ),
    );
    this.resource.reload();
    return dto;
  }

  async update(
    credentialId: number,
    body: UpdateCredentialRequest,
  ): Promise<CredentialDto> {
    const gid = this.groupService.currentGroupId();
    const dto = await firstValueFrom(
      this.http.put<CredentialDto>(
        `${environment.API_HOST}/groups/${gid}/credentials/${credentialId}`,
        body,
      ),
    );
    this.resource.reload();
    return dto;
  }

  async delete(credentialId: number): Promise<void> {
    const gid = this.groupService.currentGroupId();
    await firstValueFrom(
      this.http.delete<void>(
        `${environment.API_HOST}/groups/${gid}/credentials/${credentialId}`,
      ),
    );
    this.resource.reload();
  }
}
